import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, RotateCw, Zap, Settings, Mail, Droplets, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const SelfCleaningFiltersPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Self-Cleaning Filtration | Automatic Brush (RAB) & Vacuum (AVC) | CTC Waterworks"
        description="Compare automatic brush vs vacuum technologies for inline filtration. RAB and AVC self-cleaning systems for continuous operation without manual intervention."
        canonical="https://www.ctcwaterworks.com/filtration/self-cleaning"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Self-Cleaning Filtration</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Self-Cleaning Filtration: Two Technologies
            </h1>
            <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
              Choose automatic Brush (RAB) or automatic Vacuum (AVC) based on solids type, power/controls availability, and service preference.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Two Tech Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Two Automatic Cleaning Technologies</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* RAB Automatic Brush Card */}
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-300 rounded-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-16 h-16 bg-green-600 rounded-lg flex items-center justify-center mr-4">
                  <RotateCw className="text-white" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">RAB</h3>
                  <p className="text-sm text-gray-600">Rotating Automatic Brush</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Motorized rotating brush assembly automatically cleans the filter screen surface on demand. Sensor-triggered or timer-based operation for hands-free maintenance.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Rotating brushes mechanically scrub screen surface</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Great for fibrous and organic solids</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Consistent differential pressure control</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Simple mechanical operation</span>
                </div>
              </div>

              <Link
                to="/filtration/inline/big/brush-cleaning#automatic"
                className="flex items-center justify-between bg-white border-2 border-green-600 text-green-600 px-4 py-3 rounded-lg font-semibold hover:bg-green-600 hover:text-white transition-colors"
              >
                <span>Learn About RAB</span>
                <ArrowRight size={20} />
              </Link>
            </div>

            {/* AVC Automatic Vacuum Card */}
            <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-300 rounded-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-16 h-16 bg-orange-600 rounded-lg flex items-center justify-center mr-4">
                  <Droplets className="text-white" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">AVC</h3>
                  <p className="text-sm text-gray-600">Automatic Vacuum Cleaning</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Vacuum scanner sweeps the screen interior using differential pressure to create powerful suction. Rapid backflush cleaning cycles maintain flow under full pressure.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Vacuum nozzles with powerful suction</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Great for heavy granular solids load</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Controlled purge cycles (10-20 seconds)</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Minimal water loss during cleaning</span>
                </div>
              </div>

              <Link
                to="/filtration/inline/big/vacuum-cleaning#automatic"
                className="flex items-center justify-between bg-white border-2 border-orange-600 text-orange-600 px-4 py-3 rounded-lg font-semibold hover:bg-orange-600 hover:text-white transition-colors"
              >
                <span>Learn About AVC</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Mini-Table */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Technology Comparison</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Quick reference guide to help you choose between RAB automatic brush and AVC automatic vacuum based on your application requirements.
          </p>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-ctc-blue to-blue-700 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Technology</th>
                    <th className="px-6 py-4 text-left font-semibold">Solids Type</th>
                    <th className="px-6 py-4 text-left font-semibold">Typical Loading</th>
                    <th className="px-6 py-4 text-left font-semibold">Water Loss</th>
                    <th className="px-6 py-4 text-left font-semibold">Power/Controls</th>
                    <th className="px-6 py-4 text-left font-semibold">Best Fit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-green-50">
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-900">RAB</div>
                      <div className="text-sm text-gray-600">Automatic Brush</div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Fibrous, organic matter, algae
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Moderate, steady
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Minimal (continuous purge)
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      120V/240V motor + ΔP sensor or timer
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Surface water, canal, ponds with organic debris
                    </td>
                  </tr>
                  <tr className="hover:bg-orange-50">
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-900">AVC</div>
                      <div className="text-sm text-gray-600">Automatic Vacuum</div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Sand, silt, heavy granular
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Heavy, variable
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Very low (short backflush cycle)
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      120V/240V motor + ΔP sensor or timer
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      High solids, fine mesh screens, critical uptime needs
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border-2 border-green-300 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                <RotateCw className="mr-2 text-green-600" size={20} />
                When to Choose RAB
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Select RAB when dealing with fibrous or organic solids from surface water sources. The mechanical brush action excels at removing algae and organic matter that can stick to screens. Ideal for applications where mechanical simplicity and steady solids loading are the norm.
              </p>
            </div>

            <div className="bg-white border-2 border-orange-300 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                <Droplets className="mr-2 text-orange-600" size={20} />
                When to Choose AVC
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Choose AVC for heavy or variable solids loading, especially sand, silt, and granular debris. The vacuum system handles fine mesh screens better and completes cleaning in 10-20 seconds with minimal water loss. Best for critical applications requiring maximum uptime and frequent cleaning cycles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Benefits of Automatic Self-Cleaning</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <Zap className="text-ctc-blue mb-3" size={32} />
              <h3 className="font-bold text-gray-900 mb-2">Zero Manual Intervention</h3>
              <p className="text-gray-700 text-sm">Sensors trigger cleaning automatically—no operator presence required.</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <Settings className="text-green-600 mb-3" size={32} />
              <h3 className="font-bold text-gray-900 mb-2">Consistent Performance</h3>
              <p className="text-gray-700 text-sm">Maintains optimal differential pressure and flow rates 24/7.</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <Filter className="text-orange-600 mb-3" size={32} />
              <h3 className="font-bold text-gray-900 mb-2">Remote Site Ready</h3>
              <p className="text-gray-700 text-sm">Perfect for unattended locations where manual cleaning is impractical.</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <CheckCircle className="text-ctc-blue mb-3" size={32} />
              <h3 className="font-bold text-gray-900 mb-2">Maximum Uptime</h3>
              <p className="text-gray-700 text-sm">Continuous operation without system shutdowns for manual cleaning.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Control Options */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Control & Automation Options</h2>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-ctc-blue text-white rounded-full flex items-center justify-center font-bold mr-4">
                  1
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Differential Pressure (ΔP) Sensor</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Monitors pressure drop across the filter. When ΔP reaches the set threshold, cleaning initiates automatically. Adapts to changing water conditions and debris loads.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-ctc-blue text-white rounded-full flex items-center justify-center font-bold mr-4">
                  2
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Timer-Based Control</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Set cleaning intervals based on known water quality patterns. Ideal for predictable solids loading or scheduled maintenance windows. Can be combined with ΔP sensor for hybrid control.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-ctc-blue text-white rounded-full flex items-center justify-center font-bold mr-4">
                  3
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Power Requirements</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Both RAB and AVC require standard 120V or 240V AC power. Electric motor drives the brush rotation (RAB) or vacuum scanner (AVC). Minimal power consumption during cleaning cycles.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Integration Ready:</span> Both systems can integrate with existing SCADA, PLC, or irrigation controllers. Remote monitoring and control capabilities available for advanced installations.
            </p>
          </div>
        </div>
      </section>

      {/* BIG Series Compatibility */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-ctc-blue to-blue-700 text-white rounded-lg shadow-xl p-8">
            <h2 className="text-3xl font-bold mb-4 text-center">Both Technologies Use BIG Series Bodies</h2>
            <p className="text-blue-100 mb-6 text-center leading-relaxed">
              RAB and AVC cleaning modules mount directly to BIG Series inline filter bodies. Choose your cleaning technology independently from your filter size and screen type.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
                <h3 className="font-bold text-lg mb-2">Common Features</h3>
                <ul className="space-y-1 text-sm text-blue-100">
                  <li>• Blue Inline Gasketed design</li>
                  <li>• Multiple sizes (2" to 8" and larger)</li>
                  <li>• Custom mesh or perforation</li>
                  <li>• Stainless or epoxy-coated steel</li>
                </ul>
              </div>

              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
                <h3 className="font-bold text-lg mb-2">Field Retrofit</h3>
                <ul className="space-y-1 text-sm text-blue-100">
                  <li>• No repiping or welding required</li>
                  <li>• Upgrade manual to automatic later</li>
                  <li>• Switch between brush and vacuum</li>
                  <li>• Modular design for flexibility</li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/filtration/inline/big"
                className="inline-flex items-center bg-white text-ctc-blue px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Learn About BIG Series Bodies
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Select Your Self-Cleaning System?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Our filtration specialist team will help you choose between RAB and AVC based on your water quality, solids type, and operational requirements.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Talk to a Filtration Specialist
            </Link>

            <Link
              to="/filtration/size-my-filter"
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Settings className="mr-2" size={20} />
              Size My Filter
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SelfCleaningFiltersPage;
